<!-- @prettier -->
<template>
	<PortalWidget :widget="widget">
		<template v-slot:headerContent>
			<div class="header-extra" v-if="!showSpecificRecordDetails">
				<ColumnToggle
					:table-name="'pingTrees'"
					:user-settings="userSettings"
					:settings-status="getUserSettingsStatus"
					:column-settings="columnSettings"
					@onChange="updateUserVisibleColumns"
				/>
				<input
					v-model="filters.name.value"
					type="search"
					:placeholder="$t('SEARCH')"
					class="form-control input-md widget-header-input m-r-10"
				/>
			</div>
		</template>
		<div v-if="!isFetchingRingTrees" class="m-b-15 m-t-10">
			<!-- Create Button -->
			<button
				type="button"
				v-if="hasCreateAccess && !showSpecificRecordDetails"
				@click="showForm(null, $event)"
				data-testid="show-form-button"
				:disabled="formActive && !activeRingTree"
				class="btn btn-success btn-sm"
			>
				<i class="fa fa-plus" />
				<span v-t="'MENU_LEFT_PINGTREE_ADD'" />
			</button>

			<!-- Go to Ring Trees -->
			<button
				@click="goToRingTrees"
				v-if="showSpecificRecordDetails"
				class="btn btn-default btn-sm"
			>
				<i class="fa fa-arrow-left"></i>
				<span v-t="'PING_TREE_SHOW_ALL'"></span>
			</button>
		</div>

		<!-- Table Container -->
		<div v-if="!isFetchingRingTrees" class="grid_table_holder">
			<v-table
				:data="ringTrees"
				:current-page.sync="currentPage"
				:page-size="20"
				:filters="filters"
				:hide-sort-icons="true"
				class="table_beauty"
				@totalPagesChanged="totalPages = $event"
			>
				<thead slot="head">
					<tr>
						<th v-if="isMobile" />
						<v-th
							v-for="col in userVisibleColumns"
							:key="col"
							v-t="columnSettings[col].displayName"
							:class="columnSettings[col].class"
							:sort-key="columnSettings[col].stSort"
						/>
						<th v-if="!isMobile" v-t="'ACTIONS_COLUMN_HEADER'" />
					</tr>
				</thead>
				<tbody slot="body" slot-scope="{ displayData }">
					<!-- Empty Ring Trees row - START -->
					<tr v-if="!showAddNewForm && ringTrees && ringTrees.length == 0">
						<td colspan="100" v-t="'PING_TREES_HEADER_NONE'"></td>
					</tr>
					<!-- Empty Ring Trees row - END -->

					<!-- Create New Form -->
					<tr
						v-if="showAddNewForm && hasCreateAccess"
						class="row-inline-new-form slide-down bg-row-even"
					>
						<!-- v-if="!showNewForm" -->
						<td colspan="100">
							<RingTreeForm
								@cancel="hideForm"
								@submit="handleAddRingTreeSubmit"
								isInlineTable="true"
								:show-loader="isCreateRingTreeLoading"
							>
							</RingTreeForm>
						</td>
					</tr>
					<!-- Create new ringTree form row - END -->

					<!-- Existing ring trees list - START -->
					<template v-for="(ringTree, index) in displayData">
						<tr
							v-if="!(isMobile && activeActionRowId == ringTree.id)"
							:key="ringTree.id"
							:class="{
								'bg-row-odd': index % 2 === 0,
								'bg-row-even': index % 2 !== 0
							}"
						>
							<!-- Mobile Actions -->
							<td v-if="isMobile">
								<button
									class="btn btn-list-function btn-expand-actions"
									:disabled="showAddNewForm"
									@click="displayMobileAction(ringTree.id)"
								>
									<i class="fa fa-ellipsis-v" />
								</button>
							</td>

							<!-- name -->
							<td
								v-if="userVisibleColumns.includes('name')"
								:align="columnSettings['name'].rowAlign"
							>
								<a
									class="clickable"
									:href="getRingTreeEditHref(ringTree.id)"
									@click="showForm(ringTree, $event)"
								>
									{{ ringTree.name }}
								</a>
							</td>

							<!-- revenueDynamic -->
							<td
								v-if="userVisibleColumns.includes('revenueDynamic')"
								:align="columnSettings['revenueDynamic'].rowAlign"
							>
								{{ ringTree.revenueSettings.revenueDynamic ? 'Yes' : 'No' }}
							</td>

							<!-- minRevenueToRoute -->
							<td
								v-if="userVisibleColumns.includes('minRevenueToRoute')"
								:align="columnSettings['minRevenueToRoute'].rowAlign"
							>
								{{ formatCurrency(ringTree.minRevenueToRoute) }}
							</td>

							<!-- revenueOnFailure -->
							<td
								v-if="userVisibleColumns.includes('revenueOnFailure')"
								:align="columnSettings['revenueOnFailure'].rowAlign"
							>
								{{ formatCurrency(ringTree.revenueSettings.revenueOnFailure) }}
							</td>

							<!-- numTargets -->
							<td
								v-if="userVisibleColumns.includes('numTargets')"
								:align="columnSettings['numTargets'].rowAlign"
							>
								<span
									v-tooltip.right.hover="getTargetNamesSplitByComma(ringTree)"
									aria-hidden="true"
								>
									{{ ringTree.targets.length || 0 }}
								</span>
							</td>

							<!-- Status -->
							<td
								v-if="userVisibleColumns.includes('enabled')"
								:align="columnSettings['enabled'].rowAlign"
							>
								<Status :entity="ringTree"></Status>
							</td>

							<!-- Desktop Actions -->
							<td v-if="!isMobile">
								<RingTreeActions
									:create-access="hasCreateAccess"
									:current-ring-tree="ringTree"
									:disabled="formActive"
									:is-admin="isAdmin"
									:href="getRingTreeEditHref(ringTree.id)"
									@collapse-actions="!disabled && hideMobileAction()"
									@show-edit-form="showForm"
									@confirm-delete="openConfirmDeleteRingTreeModal"
									@set-status="setRingTreeStatus"
								/>
							</td>
						</tr>

						<!-- Edit form -->
						<tr
							v-if="ringTree.showEditForm || showSpecificRecordDetails"
							:key="ringTree.id + '-edit'"
							class="row-inline-new-form slide-down bg-row-even"
						>
							<td colspan="100">
								<RingTreeForm
									:show-loader="isUpdateRingTreeLoading"
									:ringTree="ringTree"
									@cancel="hideForm"
									@submit="handleUpdateRingTreeSubmit"
								/>
							</td>
						</tr>

						<tr
							v-if="isMobile && activeActionRowId == ringTree.id"
							:key="ringTree.id + '-mobile'"
							class="mobile-actions ng-animate-disabled slide-right"
						>
							<td colspan="10">
								<RingTreeActions
									:create-access="hasCreateAccess"
									:current-ring-tree="ringTree"
									:disabled="formActive"
									:is-admin="isAdmin"
									@collapse-actions="!disabled && hideMobileAction()"
									@show-edit-form="showForm"
									@confirm-delete="openConfirmDeleteRingTreeModal"
									@set-status="setRingTreeStatus"
								/>
							</td>
						</tr>
					</template>
				</tbody>
			</v-table>

			<!-- Pagination Starts -->
			<div class="pagination-min">
				<div class="text-center p-7">
					<smart-pagination
						:current-page.sync="currentPage"
						:total-pages="totalPages"
						:max-page-links="20"
					/>
				</div>
			</div>
			<!-- Pagination Ends -->
		</div>

		<div v-if="isFetchingRingTrees" class="text-center empty-records">
			<Loader />
			<span
				v-t="showSpecificRecordDetails ? 'PING_TREE_HEADER_LOADING' : 'PING_TREES_HEADER_LOADING'"
			/>
		</div>

		<!-- Delete Confirmation Modal Start -->
		<RingTreeDeleteModal
			v-if="showDeleteModal"
			:ring-tree="ringTreeForDelete"
			:loading-deleting-ring-tree="isRingTreeDeleteLoading"
			:loading-ring-tree-references="isRingTreeReferencesLoading"
			:ring-tree-references="ringTreeReferences"
			:check-references-text="checkReferencesText"
			:force-unlink="forceUnlink"
			@confirm="confirmDelete"
			@close="closeDeleteModal"
			@set-force-unlink="setForceUnlink"
		/>
		<!-- Delete Confirmation Modal End -->
	</PortalWidget>
</template>

<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';

import { cloneDeep, map, join } from 'lodash';

import {
	ringTreeService,
	utilities
} from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { $ngStateService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { isMobile } from '@/core/utilities';

import { STATUS_ERROR, STATUS_PENDING, STATUS_SUCCESS } from '@/store/helpers';
import * as actionTypes from '../store/actionTypes';
import { SET_RING_TREE } from '../store/mutationTypes';

import sharedMessages from '@/core/locales';
import messages from '../locales';
import ColumnToggle from '@/core/components/ColumnToggle/ColumnToggle';
import Loader from '@/core/components/Loader';
import PortalWidget from '@/core/components/PortalWidget.vue';
import Status from '@/core/components/Status/Status.vue';
import RingTreeForm from './RingTreeForm.vue';
import RingTreeActions from './RingTreeActions.vue';
import RingTreeDeleteModal from './RingTreeDeleteModal.vue';

export default {
	name: 'RingTrees',
	components: {
		Loader,
		PortalWidget,
		ColumnToggle,
		Status,
		RingTreeForm,
		RingTreeActions,
		RingTreeDeleteModal
	},
	data: () => ({
		isMobile: isMobile,
		showDeleteModal: false,
		showSpecificRecordDetails: false,
		activeRingTree: null,
		formActive: false,
		showAddNewForm: false,
		currentRingTree: {},
		filters: {
			name: { value: '', keys: ['name', 'id', 'mapToTagType', 'mapToTagName'] }
		},
		currentPage: 1,
		totalPages: 0,
		activeActionRowId: null,
		ringTreeForDelete: null,
		forceUnlink: false
	}),
	created() {
		this.setWidgetTitle({ title: this.$i18n.t('PING_TREES_HEADER') });
		this.showSpecificRecordDetails = utilities.getPropFromUrlParams(
			'pingTreeId'
		);
		this.getUserSettings();
		this.loadData();
	},
	computed: {
		...mapState(['currentAccountId']),
		...mapState('ui', ['userSettings']),
		...mapState('ringTrees', [
			'ringTrees',
			'ringTreeReferences',
			'checkReferencesText',
			'userVisibleColumns',
			'widget'
		]),
		...mapGetters({
			isAdmin: 'isAdmin',
			hasAccessToItem: 'hasAccessToItem'
		}),
		...mapGetters('ringTrees', [
			'getRingTreesStatus',
			'getRingTreeStatus',
			'isFetchingRingTrees',
			'getRingTreeReferencesStatus',
			'isRingTreeReferencesLoading',
			'isRingTreeDeleteLoading',
			'createRingTreeStatus',
			'updateRingTreeStatus',
			'deleteRingTreeStatus'
		]),
		...mapGetters('ui', ['getUserSettingsStatus']),
		columnSettings() {
			return ringTreeService.columnSettings;
		},
		isCreateRingTreeLoading() {
			return this.createRingTreeStatus.status === STATUS_PENDING;
		},
		isUpdateRingTreeLoading() {
			return this.updateRingTreeStatus.status === STATUS_PENDING;
		},
		hasCreateAccess() {
			return this.hasAccessToItem('PingTree', 'CREATE');
		},
		disabled() {
			return (
				this.isRingTreeDeleteLoading ||
				this.showAddNewForm ||
				!!this.currentRingTree.id
			);
		}
	},
	methods: {
		...mapActions('ringTrees', Object.values(actionTypes)),
		...mapActions('ui', ['getUserSettings']),
		...mapMutations('ringTrees', [SET_RING_TREE]),

		updateUserVisibleColumns() {
			this.getUserVisibleColumns();
		},
		showForm(ringTree, $event) {
			$event?.preventDefault();

			if (!this.showSpecificRecordDetails) {
				if (this.activeRingTree) {
					this.hideForm(this.activeRingTree);
				}
				this.activeRingTree = ringTree;
				this.formActive = true;
			}
			if (ringTree) {
				const _ringTree = cloneDeep(ringTree);
				this.showAddNewForm = false;
				_ringTree.showEditForm = true;
				this.updateRingTreeState(_ringTree);
			} else {
				this.showAddNewForm = true;
			}
		},
		hideForm(ringTree) {
			const _ringTree = cloneDeep(ringTree);
			if (_ringTree) {
				_ringTree.showEditForm = false;
			} else {
				this.showAddNewForm = false;
			}
			this.formActive = false;
			this.activeRingTree = null;

			this.updateRingTreeState(_ringTree);

			if (this.showSpecificRecordDetails) {
				this.goToRingTrees();
			}
		},
		updateRingTreeState(ringTree) {
			if (!ringTree) return;
			const ringTreeIndex = this.ringTrees.findIndex(
				(e) => e.id === ringTree.id
			);
			this[SET_RING_TREE]({ index: ringTreeIndex, ringTree });
		},
		async handleAddRingTreeSubmit(payload) {
			const response = await this.createRingTree(payload);

			if (this.createRingTreeStatus.status === STATUS_SUCCESS) {
				utilities.showToaster(
					STATUS_SUCCESS,
					this.$t('PING_TREE_CREATE_SUCCESS')
				);
			}
			if (this.createRingTreeStatus.status === STATUS_ERROR) {
				utilities.errorHandler(this.createRingTreeStatus.error);
			}

			return $ngStateService.go('dashboard.ringtree.new', {
				pingTreeId: response.id
			});
		},
		async handleUpdateRingTreeSubmit(payload) {
			const updatedRingTree = await this.updateRingTree(payload);

			if (this.updateRingTreeStatus.status === STATUS_SUCCESS) {
				utilities.showToaster(
					STATUS_SUCCESS,
					this.$t('PING_TREE_UPDATE_SUCCESS')
				);
				this.onUpdate(updatedRingTree);
			}

			if (this.updateRingTreeStatus.status === STATUS_ERROR) {
				utilities.errorHandler(this.updateRingTreeStatus.error);
			}
		},
		onUpdate(ringTree) {
			if (!this.showSpecificRecordDetails) {
				this.hideForm(ringTree);
			} else {
				this.updateRingTreeState(ringTree);
			}
		},
		loadData() {
			if (this.showSpecificRecordDetails) {
				this.loadRingTree(utilities.stateParams.pingTreeId);
			} else {
				this.loadRingTrees();
			}
		},
		async loadRingTree(ringTreeId) {
			await this.getRingTree({ ringTreeId });

			if (this.getRingTreeStatus.status === STATUS_SUCCESS) {
				this.showForm(this.ringTrees[0]);
			}

			if (this.getRingTreeStatus.status === STATUS_ERROR) {
				utilities.errorHandler(this.getRingTreeStatus.error);
			}
		},
		async loadRingTrees() {
			await this.getRingTrees();

			if (this.getRingTreesStatus.status === STATUS_ERROR) {
				utilities.errorHandler(this.getRingTreesStatus.error);
			}
		},
		setForceUnlink(value) {
			this.forceUnlink = value;
		},
		openConfirmDeleteRingTreeModal(ringTree) {
			this.ringTreeForDelete = ringTree;
			this.getRingTreeReferences({ ringTreeId: ringTree.id });
			this.showDeleteModal = true;
		},
		async confirmDelete() {
			await this.deleteRingTree({
				ringTree: this.ringTreeForDelete,
				forceUnlink: this.forceUnlink
			});

			if (this.deleteRingTreeStatus.status === STATUS_SUCCESS) {
				utilities.showToaster(
					STATUS_SUCCESS,
					this.$t('DELETE_PING_TREE_SUCCESSFUL_MESSAGE')
				);
				this.closeDeleteModal();
			}

			if (this.deleteRingTreeStatus.status === STATUS_ERROR) {
				utilities.errorHandler(this.deleteRingTreeStatus.error);
			}
		},
		closeDeleteModal() {
			this.showDeleteModal = false;
			this.ringTreeForDelete = null;
		},
		async setRingTreeStatus(ringTree, status) {
			if (!this.formActive) {
				const payload = { ringTree: ringTree, status: status };
				await this.updateRingTreeProperty(payload);
			}
		},
		displayMobileAction(rowId) {
			this.activeActionRowId = rowId;
		},
		hideMobileAction() {
			this.activeActionRowId = null;
		},
		goToRingTrees() {
			$ngStateService.go('dashboard.ringtrees');
		},
		getRingTreeEditHref(ringTreeId) {
			return $ngStateService.href(
				'dashboard.ringtree.edit',
				{ pingTreeId: ringTreeId },
				{ absolute: true }
			);
		},
		formatCurrency(value) {
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
				minimumFractionDigits: 2
			}).format(value || 0);
		},
		getTargetNamesSplitByComma(ringTree) {
			if (ringTree && ringTree.targets) {
				return {
					text: join(map(ringTree.targets, 'name'), ', '),
					transition: 0
				};
			}
			return { text: '', transition: 0 };
		}
	},
	i18n: {
		messages,
		sharedMessages,
		locale: 'en'
	}
};
</script>
